import { Injectable } from '@angular/core';
import { TestListEntity, TestListService } from '@nrwl/common-lib';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TestSetupConfigService } from './service/test-setup-config.service';
import { TestSetupAgentModelService } from './service/test-setup-agent-model.service';
import { TestSetupTemplateService } from './service/test-setup-template-config.service';

@Injectable()
export class TestSetupRouterService {
  private _scheduleId: string;
  private _templateId: string;
  private _testConfig: any;
  private readonly _unsubscribe: Subject<void> = new Subject();

  mode: string;
  templateName = '';
  constructor(
    private readonly testListService: TestListService,
    private readonly testConfigService: TestSetupConfigService,
    private readonly testSetupModelService: TestSetupAgentModelService,
    private readonly testSetupTemplateService: TestSetupTemplateService
  ) {}

  onDestroy(): void {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }

  //eslint-disable-next-line
  async getConfigBasedOnMode(
    //eslint-disable-next-line
    defaultParams: any,
    //eslint-disable-next-line
    agentModelConfig: any,
    testType: string
  ): Promise<any> {
    this._scheduleId = defaultParams.scheduleId;
    this.mode = defaultParams.modeType;
    this.templateName = '';
    let parsedOutput = {};
    if (this.mode === 'edit' || this.mode === 'clone') {
      parsedOutput = await this.getTestConfigById();
    } else if (this.mode === 'template') {
      this._templateId = defaultParams.templateId;
      parsedOutput = await this.getTestTemplateById();
    } else {
      parsedOutput = await this.findDefaultTemplate(testType);
      if (parsedOutput) {
        //Changing the mode as 'template' as default template found for this test type
        this.mode = 'template';
      }
    }
    if (defaultParams?.overrides) {
      parsedOutput = { parsedOutput, ...defaultParams.overrides };
    }
    if (parsedOutput) {
      // override the default agent model value with parsed output
      agentModelConfig = this.testSetupModelService.restoreDefaultValueWithOutput(
        agentModelConfig,
        parsedOutput
      );
    }
    return this.testSetupModelService.getAgentBasedConfig(agentModelConfig);
  }

  async getTestConfigById(): Promise<any> {
    try {
      const response = await this.testListService
        .getTestConfig(this._scheduleId)
        .pipe(takeUntil(this._unsubscribe))
        .toPromise();

      if (response) {
        this._testConfig = response;
        const selectedTestData: TestListEntity = this.testListService.getTestListById(
          this._scheduleId
        );
        const parsedOutput = this.testConfigService.populateTestOutputParams(
          this._testConfig,
          selectedTestData,
          this.mode
        );
        return parsedOutput;
      } else {
        return null;
      }
    } catch (e) {
      return null;
    }
  }

  async getTestTemplateById(): Promise<any> {
    try {
      const response = await this.testSetupTemplateService
        .getTemplateConfig(this._templateId)
        .pipe(takeUntil(this._unsubscribe))
        .toPromise();
      if (response.status === 'success' && response.data) {
        this.templateName = response.data.name;
        const parsedOutput = this.testSetupTemplateService.populateTemplateOutputParams(
          response.data,
          this.mode
        );
        return parsedOutput;
      }
      return null;
    } catch (e) {
      return null;
    }
  }

  async findDefaultTemplate(testType: string): Promise<any> {
    const options = {
      testType: testType,
      returnOnly: 'default'
    };
    try {
      const response = await this.testSetupTemplateService
        .findTemplateConfig(options)
        .pipe(takeUntil(this._unsubscribe))
        .toPromise();
      if (response.status === 'success' && response.data && response.data.length) {
        this.templateName = response.data[0].name;
        const parsedOutput = this.testSetupTemplateService.populateTemplateOutputParams(
          response.data[0],
          this.mode
        );
        return parsedOutput;
      } else {
        return null;
      }
    } catch (e) {
      return null;
    }
  }
}
